import type {
  InterviewWordingChange,
  InterviewWordingItem,
  InterviewWordingItemKind,
  InterviewWordingQuestion,
} from "@/types/interviewWording";

/** What each editable item is, in the editor's words. */
export const KIND_LABEL: Record<InterviewWordingItemKind, string> = {
  sectionTitle: "Section title",
  sectionPurpose: "Section purpose",
  questionLabel: "Question",
  hint: "Hint",
  textBoxLabel: "Text-box label",
  option: "Choice",
  branch: "Branch line",
};

/** The badge for a question the call fills in; null when the call never does. */
export function callSourceLabel(callSource: InterviewWordingQuestion["callSource"]): string | null {
  switch (callSource) {
    case "derived":
      return "Filled from the call";
    case "partlyDerived":
      return "Partly filled from the call";
    case "derivedWhenAnswered":
      return "Filled from the call when answered there";
    default:
      return null;
  }
}

/** One change-log row's action. A "draft" row with no `after` is a discarded draft. */
export function changeActionLabel(change: InterviewWordingChange): string {
  switch (change.action) {
    case "draft":
      return change.after == null ? "Discarded draft" : "Saved draft";
    case "publish":
      return "Published";
    case "revert":
      return "Reverted to default";
    case "codeDefaultsChanged":
      return "Code defaults changed";
    default:
      return change.action;
  }
}

/**
 * The text box's accessible name: the kind, where it sits, and — for a choice —
 * which choice. Unique on the page, so a screen reader (and a test) can tell
 * two "Choice" boxes apart.
 */
export function itemAccessibleName(item: InterviewWordingItem, ownerId: string): string {
  const base = `${KIND_LABEL[item.kind]} wording, ${ownerId}`;
  return item.optionKey ? `${base}, ${item.optionKey}` : base;
}

export type ItemStatus = "draft" | "published" | "default";

export function itemStatus(item: InterviewWordingItem): ItemStatus {
  if (item.draftText != null) return "draft";
  if (item.publishedText != null) return "published";
  return "default";
}

export const STATUS_LABEL: Record<ItemStatus, string> = {
  draft: "Draft pending",
  published: "Published",
  default: "Default",
};

/** The sentence the revert confirmation states — exactly what the action does (ruling Q7). */
export const REVERT_CONFIRM_SENTENCE =
  "This publishes the original wording for this item now, as a new version, and discards its draft.";
